// BiggestMoverCard: highlights the provider with the largest verified $/GB price change.

import React from 'react';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import type { Diff } from '../lib/api';
import { formatDeltaPct, formatUSD, deltaClass } from '../lib/format';

interface BiggestMoverCardProps {
  diff: Diff | null;
  lang: Lang;
  code: string;
}

export function BiggestMoverCard({ diff, lang, code }: BiggestMoverCardProps) {
  if (!diff) {
    return (
      <div className="card p-4 text-sm text-neutral-500">
        {t('noData', lang)}
      </div>
    );
  }

  const provider = diff.provider;
  const deltaPct = diff.delta_pct;
  const prevVal = diff.prev_value ? parseFloat(diff.prev_value) : null;
  const newVal = diff.new_value ? parseFloat(diff.new_value) : null;

  // Extract region from field: "price_per_gb::region"
  const fieldParts = diff.field.split('::');
  const region = fieldParts[1] ?? 'global';

  return (
    <div className="card p-4 border-l-2 border-l-amber-500">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-amber-600 uppercase tracking-wider">
          {t('biggestMover', lang)}
        </span>
        <span className={`text-sm font-semibold tabular-nums ${deltaClass(deltaPct)}`}>
          {formatDeltaPct(deltaPct)}
        </span>
      </div>

      <div className="flex items-start gap-4">
        <div className="flex-1">
          {provider ? (
            <Link
              to={`/providers/${provider.id}?code=${code}`}
              className="text-sm font-medium text-neutral-900 hover:text-emerald-600 transition-colors"
            >
              {provider.name}
            </Link>
          ) : (
            <span className="text-sm font-medium text-neutral-900">{diff.entity_id}</span>
          )}
          <div className="text-xs text-neutral-500 mt-0.5">{region}</div>
        </div>

        <div className="text-right">
          <div className="flex items-center gap-2 text-sm tabular-nums">
            {prevVal !== null && (
              <span className="text-neutral-500 line-through">{formatUSD(prevVal)}/GB</span>
            )}
            {newVal !== null && (
              <span className={`font-medium ${deltaClass(deltaPct)}`}>{formatUSD(newVal)}/GB</span>
            )}
          </div>
          <div className="text-xs text-neutral-500 mt-0.5">$/GB</div>
        </div>
      </div>
    </div>
  );
}
